import { isTalentActive } from "../../common/helpers/common";
import i18n from "../../i18n";
import {
  isExpertAdmin,
  isExpertCustomerService,
} from "../user/accessRightChecks";
import { User } from "../user/user";
import { ConversationType } from "./types/commands";
import { Initiator, ParticipantOption, ParticipantType } from "./types/common";
import { MessageTemplate, Participant } from "./types/queries";

export const isSystemMessage = (initiator: Initiator): boolean =>
  initiator.type === ParticipantType.SYSTEM;

export const isShiftNotificationMessage = (
  messageTemplate?: MessageTemplate,
): boolean => {
  if (!messageTemplate) return false;
  const shiftNotificationTemplates = [
    MessageTemplate.SHIFT_OPEN,
    MessageTemplate.SHIFT_FULFILLED,
    MessageTemplate.SHIFT_BUNDLE_OPEN,
    MessageTemplate.SHIFT_BUNDLE_FULFILLED,
  ];
  return shiftNotificationTemplates.includes(messageTemplate);
};

export const translate = (keyOrText: string) =>
  i18n.t(`${keyOrText}`, {
    defaultValue: keyOrText,
  });

export const canStartSupportConversations = (user: User | null) =>
  !!user && isExpertCustomerService(user) && !isExpertAdmin(user);

export const getCreateConversationType = (
  user: User | null,
  recipientType: ParticipantType,
): ConversationType => {
  if (canStartSupportConversations(user)) return ConversationType.SUPPORT;
  if (recipientType === ParticipantType.TALENT) return ConversationType.HR;
  return ConversationType.DEFAULT;
};

export const getAllowedRecipientTypes = (
  user: User | null,
): ParticipantType[] =>
  canStartSupportConversations(user)
    ? [ParticipantType.CUSTOMER, ParticipantType.TALENT]
    : [ParticipantType.TALENT];

/** Expert–customer threads have no title. Talent and HR threads keep theirs. */
export const isCustomerConversation = ({
  conversationType,
  participantTypes,
}: {
  conversationType?: ConversationType | null;
  participantTypes?: Array<ParticipantType | null | undefined>;
}): boolean =>
  conversationType === ConversationType.CUSTOMER ||
  Boolean(
    participantTypes?.some((type) => type === ParticipantType.CUSTOMER),
  );

/** Resolved talent threads stay closed unless the talent is ready for work. */
export const canReopenClosedConversation = (
  participant?: Pick<ParticipantOption, "type" | "talentStatus"> | null,
): boolean => {
  if (participant?.type !== ParticipantType.TALENT) return true;
  if (!participant.talentStatus) return true;
  return isTalentActive(participant.talentStatus);
};

export const toConversationParticipant = (
  option: ParticipantOption,
): Participant => {
  const [firstName = "", ...lastNameParts] = option.name.trim().split(/\s+/);
  return {
    id: option.id,
    firstName,
    lastName: lastNameParts.join(" "),
    type: option.type,
    city: option.city,
    customerCompanyName: option.customerCompanyName,
    talentStatus: option.talentStatus,
  };
};

type InboxLastMessage = {
  text: string;
  senderId: string;
  sender?: { firstName?: string };
} | null;

export const getInboxLastMessagePreview = ({
  lastMessage,
  currentUserId,
  draft,
  isSystem,
  youLabel,
  draftLabel,
}: {
  lastMessage: InboxLastMessage;
  currentUserId?: string;
  draft?: string;
  isSystem: boolean;
  youLabel: string;
  draftLabel: string;
}): string | null => {
  const trimmedDraft = draft?.trim();
  if (trimmedDraft) {
    return `${draftLabel}: ${trimmedDraft}`;
  }

  if (!lastMessage) return null;

  const lastMessageText = lastMessage.text || "";
  if (isSystem) {
    return lastMessageText;
  }

  const senderString =
    lastMessage.senderId === currentUserId
      ? youLabel
      : lastMessage.sender?.firstName;

  return `${senderString}: ${lastMessageText}`;
};
